import { ApiError } from '@/lib/apiError';
import { apiFetch, apiJson } from '@/lib/apiFetch';

export type PaymentInitiateResponse =
  | { clientSecret: string }
  | { alreadyPaid: true; transactionId: string };

export interface PaymentStatusResponse {
  paymentStatus: string;
  orderStatus: string;
}

export const paymentApi = {
  // Payment initiate surfaces structured { code } errors (PAYMENT_RETRY_LIMIT,
  // etc.) — keep the raw fetch path so the code attaches to ApiError.
  async initiate(orderId: string): Promise<PaymentInitiateResponse> {
    const res = await apiFetch('/payments/initiate', {
      method: 'POST',
      body: { orderId },
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      const body = (e?.message ?? e) as
        | { code?: string; message?: string }
        | string;
      const message =
        typeof body === 'string'
          ? body
          : body?.message || 'Failed to initiate payment';
      const code = typeof body === 'object' ? body?.code : undefined;
      throw new ApiError(message, res.status, code);
    }
    return (await res.json()) as PaymentInitiateResponse;
  },

  async getStatus(orderId: string): Promise<PaymentStatusResponse> {
    return apiJson<PaymentStatusResponse>(`/payments/${orderId}/status`);
  },

  async reconcile(
    orderId: string,
  ): Promise<{ reconciled: boolean; reason?: string }> {
    return apiJson<{ reconciled: boolean; reason?: string }>(
      `/payments/${orderId}/reconcile`,
      { method: 'POST' },
    );
  },

  async getDetails(orderId: string): Promise<PaymentDetailsResponse | null> {
    const res = await apiFetch(`/payments/${orderId}/details`);
    if (!res.ok) return null;
    return (await res.json()) as PaymentDetailsResponse;
  },
};

export interface PaymentDetailsResponse {
  paymentId: string;
  intentionId: string;
  transactionId: string | null;
  status: string;
  paymentMethod: string | null;
  cardBrand: string | null;
  cardLastFour: string | null;
  amountCents: number;
  currency: string;
  createdAt: string;
  paidAt: string | null;
  attemptCount: number;
}
